import { okSchema } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, CircleAlert, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { apiRequest } from '@/lib/api';
import { sessionQuery } from '@/lib/session';

export const Route = createFileRoute('/verify-email')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { token = '' } = Route.useSearch();
  const queryClient = useQueryClient();
  const verify = useMutation({
    mutationFn: () =>
      apiRequest('/api/auth/email/verify', { method: 'POST', body: { token }, schema: okSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey }),
  });
  // Tokens are single-use; make sure StrictMode's double effect doesn't burn it twice.
  const started = useRef(false);
  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      verify.mutate();
    }
  }, [token, verify]);

  return (
    <div className="flex min-h-dvh items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          {verify.isSuccess ? (
            <>
              <CheckCircle2 className="text-success mb-2 size-8" />
              <CardTitle>Email verified</CardTitle>
              <CardDescription>Thanks for confirming your email address.</CardDescription>
            </>
          ) : verify.isError || !token ? (
            <>
              <CircleAlert className="text-destructive mb-2 size-8" />
              <CardTitle>Couldn&apos;t verify</CardTitle>
              <CardDescription>
                {token ? verify.error?.message : 'This link is missing its token.'} You can request
                a new link from your account page.
              </CardDescription>
            </>
          ) : (
            <>
              <Loader2 className="text-muted-foreground mb-2 size-8 animate-spin" />
              <CardTitle>Verifying…</CardTitle>
            </>
          )}
        </CardHeader>
        <CardFooter>
          <Button asChild className="w-full" variant={verify.isSuccess ? 'default' : 'outline'}>
            <Link to="/">Continue to Hearth</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
